<template>
  <div id="app">
    <v-app>
      <BrowserCompatDialog
        ref="browserCompat"
        :lightTheme="!browserCompatLightTheme"
      />
      <template v-if="!globalLoading">
        <ExternalBanner />
        <Header v-if="showHeader" />
        <GlobalToastMessage />
        <TermsDialog ref="terms" />
        <TokenAuthErrorDialog />
        <DealerCCRequiredDialog />
        <v-main>
          <v-fade-transition hide-on-leave>
            <router-view :key="$route.fullPath" />
          </v-fade-transition>
        </v-main>
        <Footer
          @openBrowserCompatDialog="openBrowserCompatDialog"
          @openTermsDialog="openTermsDialog"
          v-if="showFooter"
        />
        <AiAssistant v-if="showAiAssistant" />
      </template>
      <template v-else>
        <v-fade-transition hide-on-leave>
          <v-container
            fill-height
            style="background-color: black; height: 100vh"
          >
            <v-row align="center" justify="center">
              <v-col cols="8">
                <v-img
                  contain
                  src="@/assets/svg/3xLogo.svg"
                  :aspect-ratio="2.38"
                >
                </v-img>
                <v-progress-linear
                  indeterminate
                  color="primary"
                  :size="150"
                  style="margin: auto"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-fade-transition>
      </template>
    </v-app>
  </div>
</template>

<script lang="ts">
import './scss/video_overrides.scss'
import './scss/vuetify_overrides.scss'
import { defineComponent } from 'vue'
import Header from '@/components/global/Header.vue'
import Footer from '@/components/global/Footer.vue'
import BrowserCompatDialog from '@/components/global/BrowserCompatDialog.vue'
import GlobalToastMessage from '@/components/global/GlobalToastMessage.vue'
import TermsDialog from '@/components/global/termsDialog/TermsDialog.vue'
import DealerCCRequiredDialog from '@/components/banners/DealerCCRequiredDialog.vue'
import AiAssistant from '@/components/chat/AiAssistant.vue'
import TokenAuthErrorDialog from '@/components/global/TokenAuthErrorDialog.vue'
import { initClient } from '@/utils/webSockets'
import ExternalBanner from '@/components/global/ExternalBanner.vue'
import { Settings } from 'luxon'

import { mapActions, mapGetters, mapState } from 'vuex'

const FEATURE_UPDATE_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export default defineComponent({
  name: 'App',
  components: {
    Header,
    Footer,
    DealerCCRequiredDialog,
    GlobalToastMessage,
    BrowserCompatDialog,
    TermsDialog,
    AiAssistant,
    TokenAuthErrorDialog,
    ExternalBanner,
  },
  data() {
    return {
      featureFlagRefreshHandle: null,
    }
  },
  computed: {
    ...mapState('Security', [
      'isLoggedIn',
      'globalLoading',
      'user',
      'isTokenAuthenticated',
      'tokenAccessDenied',
    ]),
    ...mapGetters('Security', ['isDealerUser']),
    ...mapState('ExternalCases', ['externalUserIsLoggedIn']),
    ...mapGetters('Environment', ['hasAssistant']),
    externalCloudCases() {
      return this.isTokenAuthenticated
    },
    showAiAssistant() {
      return this.isDealerUser && this.isLoggedIn && this.hasAssistant
    },
    showHeader() {
      return this.$route.meta.showHeader != null &&
        this.$route.meta.showHeader === false
        ? false
        : true
    },
    showFooter() {
      return this.$route.meta.showFooter != null &&
        this.$route.meta.showFooter === false
        ? false
        : true
    },
    browserCompatLightTheme() {
      return !this.isLoggedIn || !this.externalUserIsLoggedIn
    },
  },
  methods: {
    ...mapActions('Security', ['checkSystemStatus', 'getOwnUser']),
    ...mapActions('Environment', ['refreshFeatures', 'refreshSettings']),
    openBrowserCompatDialog() {
      this.$refs.browserCompat.openDialog()
    },
    openTermsDialog() {
      this.$refs.terms.userClick()
    },
    setupFeatureFlagLoader() {
      this.refreshFeatures()
      this.featureFlagRefreshHandle = setInterval(() => {
        this.refreshFeatures()
        if (this.isLoggedIn && !this.externalCloudCases) this.refreshSettings()
      }, FEATURE_UPDATE_INTERVAL_MS)
    },
    setI18nLocale() {
      const locale = this.user.locale ?? 'en'
      localStorage.setItem('language', locale)
      this.$i18n.locale = locale
      Settings.defaultLocale = locale
      this.$vuetify.lang.current = locale
    },
  },
  created() {
    this.checkSystemStatus()
    this.setupFeatureFlagLoader()
    if (this.$route.name === 'Login' || this.$route.name === 'OidcCallback') {
      this.getOwnUser().then(() => {
        if (this.tokenAccessDenied) {
          this.$router.push('/no-access')
        } else if (this.isLoggedIn) {
          if (this.user.isDealerUser) {
            this.$router.push('/customers')
          } else if (this.user.isInternalUser) {
            this.$router.push('/dealers')
          } else {
            this.$router.push('/dashboard')
          }
        }
      })
    }
  },
  destroyed() {
    clearInterval(this.featureFlagRefreshHandle)
  },
  watch: {
    isLoggedIn() {
      if (this.isLoggedIn && !this.externalCloudCases) {
        this.setI18nLocale()
        initClient()
      }
    },
    externalUserIsLoggedIn() {
      if (this.externalUserIsLoggedIn && !this.externalCloudCases) {
        initClient()
      }
    },
  },
})
</script>
<style lang="scss">
* {
  text-transform: none !important;
}
a {
  text-decoration: none;
}
body {
  height: 100%;
  .break-long-word {
    word-break: break-word;
  }
  .v-application {
    .v-application--wrap {
      height: 100% !important;
    }
    font-family: 'IBM Plex Sans', sans-serif !important;
  }
}
html {
  background-color: #000;
  width: 100%;
  margin: 0;
  .container {
    @media screen and (min-width: $medium) {
      max-width: unset;
    }
  }
}

.loading-icon {
}
</style>
